import {
  ALERT_MANAGER_EVENT_TYPE_LOADING_OVERLAY,
  INSTANCE_DEFAULT,
} from '../../utils/spds/constants/alert-manager';
import { LOADING_OVERLAY_FULL_VIEWPORT_ID } from '../../utils/spds/constants/loading-overlay';
import { assert } from '@ember/debug';
import Service, { service } from '@ember/service';

/**
 * SPDS Loading Overlay Service
 *
 * Programmatic API for showing/hiding `Spds::Indicator::LoadingOverlay` instances
 * anchored to a specific element or covering the full viewport. The container
 * (`Spds::Feedback::LoadingOverlay::Container`) renders entries broadcast through
 * the alert manager.
 *
 * The service only ever broadcasts an `id` (string) plus presentational options;
 * it never holds element references. The container resolves the render target by
 * id at render time, falling back to `#ember-testing` / `document.body` when no
 * anchor with that id exists. That fallback is also how the full-viewport entry
 * is routed: it is broadcast under a sentinel id that intentionally never matches
 * a real DOM node, so the container renders it inside the top-level fallback
 * target with `@fixed={{true}}`.
 *
 * Call shape determines the rendering target:
 *
 * - `show(elementOrId, options?)` - anchored to the given element / id. When
 *   passing an element, `element.id` is reused; engineers calling the service
 *   directly must guarantee an id (the `{{spds-loading-overlay}}` modifier sets
 *   one automatically).
 * - `show()` / `show(options)` - full-viewport.
 *
 * `update` and `hide` mirror the same discriminator.
 *
 * @class SpdsLoadingOverlayService
 * @extends Service
 *
 * @example Element-scoped
 * this.spdsLoadingOverlay.show(this.listEl, { ariaLabel: 'Loading clients' });
 * this.spdsLoadingOverlay.hide(this.listEl);
 *
 * @example Full-viewport
 * this.spdsLoadingOverlay.show();
 * this.spdsLoadingOverlay.show({ ariaLabel: 'Loading data' });
 * this.spdsLoadingOverlay.hide();
 */
export default class SpdsLoadingOverlayService extends Service {
  @service alertManager;

  /**
   * Show a loading overlay. See class docs for call shapes.
   *
   * @param {HTMLElement|string|object} [anchorOrOptions] - Anchor element / id, or
   *   options object for a full-viewport overlay.
   * @param {object} [options] - Overlay options when the first arg is an anchor.
   * @param {string} [options.ariaLabel] - Override the spinner's accessible label.
   * @param {string} [options.color] - Spinner color variant.
   * @param {string} [options.size] - Spinner size variant.
   */
  show = (anchorOrOptions, options) => {
    let { id, payload } = this.#parseArgs(anchorOrOptions, options);

    this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_LOADING_OVERLAY, {
      id,
      ...payload,
    });
  };

  /**
   * Update an existing loading overlay's options.
   *
   * @param {HTMLElement|string|object} [anchorOrOptions] - Same shape as {@link show}.
   * @param {object} [options] - Same shape as {@link show}.
   */
  update = (anchorOrOptions, options) => {
    let { id, payload } = this.#parseArgs(anchorOrOptions, options);

    this.alertManager.broadcast(ALERT_MANAGER_EVENT_TYPE_LOADING_OVERLAY, {
      id,
      event: 'update',
      ...payload,
    });
  };

  /**
   * Hide a loading overlay. Pass the same element / id used in {@link show} to
   * dismiss an element-scoped overlay; call with no args to dismiss the
   * full-viewport overlay.
   *
   * @param {HTMLElement|string} [anchor] - Anchor element / id to dismiss; omit
   *   for full-viewport.
   */
  hide = async anchor => {
    let id = anchor ? this.#resolveId(anchor) : LOADING_OVERLAY_FULL_VIEWPORT_ID;

    try {
      await this.alertManager.clear(ALERT_MANAGER_EVENT_TYPE_LOADING_OVERLAY, INSTANCE_DEFAULT, {
        id,
      });
    } catch {
      // Container may already be destroyed during teardown
    }
  };

  /**
   * Normalizes show/update arguments into `{ id, payload }`. The service never
   * stores or forwards the element reference itself — only its id makes it onto
   * the broadcast payload.
   * @private
   */
  #parseArgs(anchorOrOptions, options) {
    if (anchorOrOptions instanceof HTMLElement || typeof anchorOrOptions === 'string') {
      return {
        id: this.#resolveId(anchorOrOptions),
        payload: { ...options },
      };
    }

    return {
      id: LOADING_OVERLAY_FULL_VIEWPORT_ID,
      payload: { ...anchorOrOptions },
    };
  }

  /**
   * Returns the id for an anchor. When given an element, reuses `element.id`
   * (asserting in dev when missing). When given a string, treats it as the id
   * directly.
   * @private
   */
  #resolveId(anchor) {
    if (typeof anchor === 'string') return anchor;

    assert(
      'spds/loading-overlay: anchor element must have an `id` attribute. Use the `{{spds-loading-overlay}}` modifier to have one assigned automatically.',
      Boolean(anchor.id)
    );

    return anchor.id;
  }
}
